import { useEffect, useState } from 'react'
import { client } from '../lib/api'
import {
  PHOTO_REQUEST_CATEGORY_OPTIONS,
  PHOTO_REQUEST_TAGLINE,
  PHOTO_REQUEST_WHO_OPTIONS,
  type PhotoRequestCategory,
  type PhotoRequestSettings,
  type PhotoRequestWhoCan,
} from '../../shared/photo-requests'

export function PhotoRequestSettingsPanel({
  notify,
  onSaved,
}: {
  notify: (msg: string) => void
  onSaved?: () => void
}) {
  const [settings, setSettings] = useState<PhotoRequestSettings | null>(null)
  const [excludedInput, setExcludedInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    void client
      .photoRequestSettings()
      .then((r) => {
        setSettings(r.settings)
        setExcludedInput(r.settings.excludedTerms.join(', '))
      })
      .catch((e) => notify((e as Error).message))
      .finally(() => setLoading(false))
  }, [notify])

  const toggleCategory = (value: PhotoRequestCategory) => {
    if (!settings) return
    setSettings((s) => {
      if (!s) return s
      const has = s.allowedCategories.includes(value)
      const next = has ? s.allowedCategories.filter((c) => c !== value) : [...s.allowedCategories, value]
      return { ...s, allowedCategories: next.length ? next : s.allowedCategories }
    })
  }

  const save = async () => {
    if (!settings) return
    setSaving(true)
    try {
      const excludedTerms = excludedInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
        .slice(0, 20)
      await client.updatePhotoRequestSettings({ ...settings, excludedTerms })
      notify('Photo request settings saved')
      onSaved?.()
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  if (loading || !settings) {
    return <p className="page-sub">Loading photo request settings…</p>
  }

  return (
    <div className="settings-form">
      <p className="page-sub">{PHOTO_REQUEST_TAGLINE}</p>
      <p className="field-hint">
        Clothed, non-explicit custom photos only. You stay in control — decline or counter any time, no penalty.
        Reuse is private viewing in chat by default.
      </p>

      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={settings.enabled}
          onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
        />
        Accept paid photo requests from matches
      </label>

      <div className="field">
        <label>Who can request</label>
        <div className="segmented">
          {PHOTO_REQUEST_WHO_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={settings.whoCanRequest === opt.value ? 'selected' : ''}
              onClick={() => setSettings({ ...settings, whoCanRequest: opt.value as PhotoRequestWhoCan })}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <label>Allowed categories</label>
        <div className="chip-row">
          {PHOTO_REQUEST_CATEGORY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              title={opt.hint}
              className={`chip ${settings.allowedCategories.includes(opt.value) ? 'selected' : ''}`}
              onClick={() => toggleCategory(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <label>Never allowed (custom exclusions)</label>
        <input
          className="input"
          value={excludedInput}
          onChange={(e) => setExcludedInput(e.target.value)}
          placeholder="e.g. gym mirror, work uniform, pet photos"
        />
        <span className="field-hint">Comma-separated. Request composer blocks these before send.</span>
      </div>

      <div className="field">
        <label>Minimum amount (credits)</label>
        <input
          className="input"
          type="number"
          min={10}
          max={500}
          value={settings.minAmountCredits}
          onChange={(e) => setSettings({ ...settings, minAmountCredits: Number(e.target.value) })}
        />
      </div>

      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={settings.faceRequired}
          onChange={(e) => setSettings({ ...settings, faceRequired: e.target.checked })}
        />
        Face required in fulfillment photos
      </label>

      <div className="field">
        <label>Request expiry</label>
        <div className="segmented">
          {[24, 48].map((h) => (
            <button
              key={h}
              type="button"
              className={settings.requestExpiryHours === h ? 'selected' : ''}
              onClick={() => setSettings({ ...settings, requestExpiryHours: h as 24 | 48 })}
            >
              {h} hours
            </button>
          ))}
        </div>
      </div>

      <button className="button primary" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save photo request settings'}
      </button>
    </div>
  )
}
